import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  FiMenu,
  FiHome,
  FiClipboard,
  FiUsers,
  FiLogOut,
  FiSearch,
  FiBell,
} from "react-icons/fi";

export default function ProjectManagerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const toggleSidebar = () => setSidebarOpen((s) => !s);

  const logout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const pageTitle = useMemo(() => {
    if (location.pathname.includes("/projects")) return "Projects";
    if (location.pathname.includes("/tasks")) return "Employee Tasks";
    if (location.pathname.includes("/dashboard")) return "Dashboard";
    return "Project Manager";
  }, [location.pathname]);

  const linkBase =
    "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all";
  const linkActive =
    "bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-100";
  const linkInactive =
    "text-gray-700 hover:bg-gray-100 hover:text-gray-900";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="flex h-screen">
        {/* Sidebar */}
        <aside
          className={`relative border-r bg-white/80 backdrop-blur-md shadow-sm transition-all duration-300 ${
            sidebarOpen ? "w-72" : "w-20"
          }`}
        >
          {/* Brand */}
          <div className="flex items-center justify-between px-4 py-4 border-b">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl bg-indigo-600 text-white grid place-items-center shadow">
                PM
              </div>
              {sidebarOpen && (
                <div className="leading-tight">
                  <div className="text-sm font-semibold text-gray-900">
                    PM Dashboard
                  </div>
                  <div className="text-xs text-gray-500">Management Panel</div>
                </div>
              )}
            </div>

            <button
              onClick={toggleSidebar}
              className="p-2 rounded-xl hover:bg-gray-100 transition"
              aria-label="Toggle sidebar"
            >
              <FiMenu size={18} />
            </button>
          </div>

          {/* Nav */}
          <nav className="px-3 py-4 space-y-2">
            <NavLink
              to="dashboard"
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : linkInactive} ${
                  !sidebarOpen ? "justify-center px-0" : ""
                }`
              }
            >
              <FiHome className="shrink-0" size={18} />
              {sidebarOpen && <span>Home</span>}
            </NavLink>

            <NavLink
              to="projects"
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : linkInactive} ${
                  !sidebarOpen ? "justify-center px-0" : ""
                }`
              }
            >
              <FiClipboard className="shrink-0" size={18} />
              {sidebarOpen && <span>Projects</span>}
            </NavLink>

            <NavLink
              to="tasks"
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : linkInactive} ${
                  !sidebarOpen ? "justify-center px-0" : ""
                }`
              }
            >
              <FiUsers className="shrink-0" size={18} />
              {sidebarOpen && <span>Employees Tasks</span>}
            </NavLink>
          </nav>

          {/* Logout (bottom) */}
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t bg-white/70 backdrop-blur-md">
            <button
              onClick={logout}
              className={`w-full ${linkBase} text-red-600 hover:bg-red-50 hover:text-red-700 ${
                !sidebarOpen ? "justify-center px-0" : ""
              }`}
            >
              <FiLogOut className="shrink-0" size={18} />
              {sidebarOpen && <span>Logout</span>}
            </button>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Topbar */}
          <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b">
            <div className="flex items-center justify-between px-4 md:px-6 py-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleSidebar}
                  className="md:hidden p-2 rounded-xl hover:bg-gray-100 transition"
                  aria-label="Toggle sidebar"
                >
                  <FiMenu size={18} />
                </button>

                <div>
                  <h1 className="text-lg md:text-xl font-semibold text-gray-900">
                    {pageTitle}
                  </h1>
                  <p className="text-xs text-gray-500">
                    Welcome back, manage everything smoothly
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Search (desktop) */}
                <div className="hidden md:flex items-center gap-2 rounded-2xl bg-gray-100 px-3 py-2">
                  <FiSearch className="text-gray-500" />
                  <input
                    className="bg-transparent outline-none text-sm w-56 placeholder:text-gray-400"
                    placeholder="Search projects, tasks..."
                  />
                </div>

                <button className="p-2 rounded-xl hover:bg-gray-100 transition relative">
                  <FiBell size={18} />
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-indigo-600 text-white text-[10px] grid place-items-center">
                    3
                  </span>
                </button>

                <div className="flex items-center gap-2 rounded-2xl bg-gray-100 px-3 py-2">
                  <div className="h-8 w-8 rounded-xl bg-gray-900 text-white grid place-items-center text-xs font-semibold">
                    PM
                  </div>
                  <div className="hidden sm:block leading-tight">
                    <div className="text-sm font-medium text-gray-900">
                      Project Manager
                    </div>
                    <div className="text-xs text-gray-500">Admin</div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 overflow-auto p-4 md:p-6">
            <div className="mx-auto max-w-6xl">
              {/* Content container card */}
              <div className="rounded-3xl bg-white shadow-sm border p-4 md:p-6">
                <Outlet />
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
